import React from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';
import SEO from '../components/SEO';
import Gallery from '../components/Gallery';

export default function ServiceCanine() {
  const { getPage } = useCMS();
  const page = getPage('kiropraktik-til-hunde');

  if (!page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-48 bg-brand-sand/40 rounded" />
          <div className="h-4 w-64 bg-brand-sand/30 rounded" />
        </div>
      </div>
    );
  }

  const { title, content, meta_title, meta_description } = page;

  // Hjælper til Cloudflare on-the-fly resizing af billeder
  const getResizedUrl = (url, width = 1200, quality = 80) => {
    return url.replace(
      'https://assets.auxodev.dk/assets/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/assets/`
    );
  };

  const mainImage = 'https://assets.auxodev.dk/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/canine-portrait.jpg';

  return (
    <>
      <SEO 
        title={title?.da || 'Kiropraktik til Hunde'} 
        description={meta_description?.da} 
      />

      {/* Header Sektion */}
      <section className="bg-brand-beige/30 border-b border-brand-sand/30 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-brown mb-3 block">
            Ydelser &bull; Hunde
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-brand-dark">
            {title?.da}
          </h1>
          <p className="text-brand-brown text-sm md:text-base mt-4 max-w-xl mx-auto">
            Blid og professionel kiropraktisk behandling, der optimerer din hunds bevægelighed, smertelindring og livskvalitet.
          </p>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section className="py-16 md:py-24 bg-brand-cream">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Tekstindhold */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div 
                className="prose prose-stone prose-headings:font-serif prose-headings:font-bold prose-headings:text-brand-dark prose-p:text-brand-brown prose-p:leading-relaxed prose-li:text-brand-brown prose-strong:text-brand-dark max-w-none"
                dangerouslySetInnerHTML={{ __html: content?.da || '' }}
              />

              <div className="mt-6 p-6 rounded-brand bg-brand-beige/40 border border-brand-sand/40">
                <h3 className="font-serif text-lg font-bold text-brand-dark mb-3">
                  Hvilke hunde har gavn af kiropraktik?
                </h3>
                <p className="text-brand-brown text-sm leading-relaxed mb-4">
                  Kiropraktik er velegnet til alle typer hunde, uanset race, størrelse og alder. Det er særligt gavnligt for:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-brand-brown">
                  <div className="flex gap-2.5">
                    <i className="fa-regular fa-dog text-brand-brown/80 mt-0.5 shrink-0" />
                    <p><strong>Familiehunden:</strong> Bevarer smidighed og bevægelighed, så hunden kan lege og gå ture uden ubehag.</p>
                  </div>
                  <div className="flex gap-2.5">
                    <i className="fa-regular fa-trophy text-brand-brown/80 mt-0.5 shrink-0" />
                    <p><strong>Brugs- og sportshunde:</strong> Optimerer præstation, koordination og forebygger skader under hård træning.</p>
                  </div>
                  <div className="flex gap-2.5">
                    <i className="fa-regular fa-cane text-brand-brown/80 mt-0.5 shrink-0" />
                    <p><strong>Den ældre hund:</strong> Lindrer stivhed og smerter i forbindelse med slidgigt og aldersbetingede forandringer.</p>
                  </div>
                  <div className="flex gap-2.5">
                    <i className="fa-regular fa-kit-medical text-brand-brown/80 mt-0.5 shrink-0" />
                    <p><strong>Genoptræning:</strong> Understøtter kroppen efter operationer, diskusprolaps eller ledskader.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billede og Info-boks */}
            <div className="lg:col-span-5 flex flex-col gap-8 lg:sticky lg:top-24">
              <div className="aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/3] rounded-brand overflow-hidden border border-brand-sand/60 shadow-lg bg-brand-sand/20">
                <img
                  src={getResizedUrl(mainImage, 800, 85)}
                  alt="Rolig hund i trygge rammer hos EquiGramVet"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="p-6 rounded-brand border border-brand-sand/50 bg-brand-cream shadow-sm">
                <h4 className="font-serif text-base font-bold text-brand-dark mb-3">
                  Behandling i trygge rammer
                </h4>
                <p className="text-brand-brown text-xs leading-relaxed mb-3">
                  Vi lægger stor vægt på, at hunden føler sig tryg. Behandlingen foregår på gulvet eller på et skridsikkert underlag, og vi tager os altid god tid til at lade hunden snuse og vænne sig til situationen.
                </p>
                <div className="flex items-center gap-2 text-xs text-brand-dark font-semibold">
                  <i className="fa-regular fa-shield-heart text-brand-brown" />
                  <span>Altid blide, kontrollerede og smertefrie teknikker.</span>
                </div>
              </div>

              <div className="p-6 rounded-brand bg-brand-beige/50 border border-brand-sand/60 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-brown">Særtilbud resten af 2026</span>
                  <span className="font-serif font-bold text-lg text-brand-dark">Fra 399.-</span>
                </div>
                <p className="text-xs text-brand-brown leading-relaxed">
                  Første behandling af hund 399.- (20% rabat ved ekstra hund samme sted). Opfølgende 249.-. Inkl. moms.
                </p>
                <Link
                  to="/priser"
                  className="mt-1 text-center py-2 px-4 bg-brand-dark text-brand-cream text-xs font-medium rounded-brand hover:bg-brand-brown transition-colors"
                >
                  Se alle priser og betingelser
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Galleri Sektion */}
      <section className="py-16 md:py-20 bg-brand-beige/20 border-t border-brand-sand/30">
        <div className="mx-auto max-w-7xl px-6">
          <Gallery gallerySlug="kiropraktik-hunde" />
        </div>
      </section>
    </>
  );
}
